import { useAuth } from "@Auth/useAuth";
import { DeleteModal } from "@common/DeleteModal/DeleteModal";
import { DRAWER_CONTEXT } from "@models/DrawerContext";
import { useOrdersStore } from "@stores/ordersStore";
import { useDrawer } from "@hooks/useDrawer";
import { onInfoShow } from "@utils/onInfoShow";
import { onErrorShow } from "@utils/onErrorShow";
import { useMemo, useState, useEffect, useCallback } from "react";
import { OrdersClient } from "../clients/OrdersClient";
import { useNavigate } from "react-router-dom";
import { supabase } from "@supabaseClient/supabaseClient";
import {
  CustomFieldsClient,
  ICustomFieldDefinition,
  ICustomFieldValue,
} from "@Clients/CustomFieldsClient";
import { OrganizationSettingsClient } from "@Clients/OrganizationSettingsClient";
import { CustomFieldsManager } from "@common/CustomFields/CustomFieldsManager";
import { Dropdown, DropdownOption } from "@components/Dropdown/Dropdown";

import {
  Box,
  Button,
  Chip,
  Divider,
  styled,
  Typography,
  Dialog,
  IconButton,
  Tabs,
  Tab,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { TableLoadingIndicator } from "@Layout/ViewAllLayout/TableLoadingIndicator";
import { NoContentCard } from "@common/ComplexPage/NoContentCard";
import { AgGridReact } from "ag-grid-react";
import { useAgGridColumns } from "@utils/generateAgGridColumns";
import { AgGridTableContainer } from "@common/AgGridTableContainer";
import { useDebounce } from "@hooks/useDebounce";
import { InputField } from "@common/InputField";
import {
  CheckCircle,
  Pending,
  Cancel,
  PlayArrow,
  Close,
  Settings,
} from "@mui/icons-material";
import { InventoryUpdateData, OrdersTableRow } from "@models/OrderDetailTypes";
import {
  SelectionChangedEvent,
  ColDef,
  ValueGetterParams,
  ICellRendererParams,
} from "ag-grid-community";

// Use the proper interface from models
type RowDataType = OrdersTableRow;

const ordersFieldAndHeaderNames = {
  order_number: "Order #",
  customer_name: "Customer",
  customer_email: "Email",
  status: "Status",
  total_items: "Items",
  created_at: "Created",
  po_number: "PO Number",
  due_date: "Due Date",
  customer_reference: "Customer Reference",
};

const mobileColumns = ["order_number", "customer_name", "status"];

const getCustomFieldValue = (value: ICustomFieldValue) => {
  if (value.value_text !== null && value.value_text !== undefined)
    return value.value_text;
  if (value.value_number !== null && value.value_number !== undefined)
    return value.value_number;
  if (value.value_bool !== null && value.value_bool !== undefined)
    return value.value_bool;
  if (value.value_date !== null && value.value_date !== undefined)
    return value.value_date;
  if (value.value_json !== null && value.value_json !== undefined)
    return value.value_json;
  return null;
};

const getStatusColor = (status: string) => {
  switch (status) {
    case "open":
    case "draft":
    case "confirmed":
      return "primary";
    case "processing":
    case "in_progress":
      return "warning";
    case "completed":
      return "success";
    case "cancelled":
      return "error";
    default:
      return "default";
  }
};

export default function AllOrders() {
  const { open } = useDrawer();
  // Selected row for the delete action is local page state, not drawer state.
  const [selectedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { baseUser } = useAuth();
  const { orders, loading, getOrdersByOrganizationId } = useOrdersStore();
  const [isOpenConfirmationModal, setIsOpenConfirmationModal] = useState(false);
  const [isOpenCustomColumnsModal, setIsOpenCustomColumnsModal] =
    useState(false);
  const [settingsTab, setSettingsTab] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);

  // Column visibility state - will be loaded from database
  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >({
    order_number: true,
    customer_name: true,
    customer_email: true,
    status: true,
    total_items: true,
    created_at: true,
    po_number: true,
    due_date: true,
    customer_reference: true,
  });
  const [customColumns, setCustomColumns] = useState<ICustomFieldDefinition[]>(
    []
  );
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<
    ICustomFieldValue[]
  >([]);
  const [hasLoadedColumnVisibility, setHasLoadedColumnVisibility] =
    useState(false);

  // Status filter options
  const statusOptions: DropdownOption[] = [
    { value: "", label: "All Statuses" },
    { value: "open", label: "Open", icon: <Pending color="primary" /> },
    {
      value: "processing",
      label: "Processing",
      icon: <PlayArrow color="warning" />,
    },
    {
      value: "completed",
      label: "Completed",
      icon: <CheckCircle color="success" />,
    },
    { value: "cancelled", label: "Cancelled", icon: <Cancel color="error" /> },
  ];

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const fetchCustomColumns = useCallback(async () => {
    if (!baseUser?.organization_id) return;
    setIsLoadingColumns(true);
    try {
      const columns = await CustomFieldsClient.getDefinitionsByOrganizationId(
        baseUser.organization_id,
        "order"
      );
      setCustomColumns(columns);

      // Initialize visibility for new custom columns (default to visible)
      setColumnVisibility((prev) => {
        const next = { ...prev };
        let hasNewColumns = false;
        columns.forEach((col) => {
          if (next[col.name] === undefined) {
            next[col.name] = true;
            hasNewColumns = true;
          }
        });
        if (hasNewColumns && hasLoadedColumnVisibility) {
          OrganizationSettingsClient.setSetting(
            baseUser.organization_id,
            "orders_table_column_visibility",
            next
          ).catch((error) => {
            console.error("Error saving column visibility:", error);
          });
        }
        return next;
      });
    } catch (error) {
      console.error("Error fetching custom columns:", error);
      onErrorShow("Failed to load custom columns");
    } finally {
      setIsLoadingColumns(false);
    }
  }, [baseUser?.organization_id, hasLoadedColumnVisibility]);

  const loadColumnVisibility = useCallback(async () => {
    if (!baseUser?.organization_id) return;

    try {
      const setting = await OrganizationSettingsClient.getSetting(
        baseUser.organization_id,
        "orders_table_column_visibility"
      );

      if (setting && setting.setting_value) {
        // Merge with defaults to ensure all columns have a value
        setColumnVisibility({
          order_number: true,
          customer_name: true,
          customer_email: true,
          status: true,
          total_items: true,
          created_at: true,
          po_number: true,
          due_date: true,
          customer_reference: true,
          ...setting.setting_value,
        });
      }
    } catch (error) {
      console.error("Error loading column visibility:", error);
      // Keep default values on error
    } finally {
      setHasLoadedColumnVisibility(true);
    }
  }, [baseUser?.organization_id]);
  // Load column visibility and custom columns on mount
  useEffect(() => {
    if (baseUser?.organization_id) {
      loadColumnVisibility();
      fetchCustomColumns();
    }
  }, [baseUser?.organization_id, loadColumnVisibility, fetchCustomColumns]);

  // Fetch custom columns when modal opens
  useEffect(() => {
    if (isOpenCustomColumnsModal && baseUser?.organization_id) {
      fetchCustomColumns();
    }
  }, [isOpenCustomColumnsModal, baseUser?.organization_id, fetchCustomColumns]);

  useEffect(() => {
    const fetchCustomFieldValues = async () => {
      if (!baseUser?.organization_id || orders.length === 0) {
        setCustomFieldValues([]);
        return;
      }
      try {
        const values = await CustomFieldsClient.getValuesForEntities(
          baseUser.organization_id,
          "order",
          orders.map((order) => order.id)
        );
        setCustomFieldValues(values);
      } catch (error) {
        console.error("Error fetching order custom field values:", error);
      }
    };
    fetchCustomFieldValues();
  }, [baseUser?.organization_id, orders]);

  const handleColumnVisibilityChange = async (
    field: string,
    visible: boolean
  ) => {
    if (!baseUser?.organization_id) return;

    const newVisibility = {
      ...columnVisibility,
      [field]: visible,
    };
    setColumnVisibility(newVisibility);

    // Save to database
    try {
      await OrganizationSettingsClient.setSetting(
        baseUser.organization_id,
        "orders_table_column_visibility",
        newVisibility
      );
    } catch (error) {
      console.error("Error saving column visibility:", error);
      onErrorShow("Failed to save column visibility settings");
      // Revert on error
      setColumnVisibility(columnVisibility);
    }
  };

  const baseColumns = useAgGridColumns(
    ordersFieldAndHeaderNames,
    mobileColumns
  );

  const customFieldValueMap = useMemo(() => {
    const definitionMap = new Map(
      customColumns.map((column) => [column.id, column])
    );
    const valueMap: Record<
      string,
      Record<string, string | number | boolean | null | undefined>
    > = {};
    customFieldValues.forEach((value) => {
      const definition = definitionMap.get(value.field_id);
      if (!definition) return;
      const resolvedValue = getCustomFieldValue(value);
      if (!valueMap[value.entity_id]) {
        valueMap[value.entity_id] = {};
      }
      valueMap[value.entity_id][definition.name] =
        resolvedValue as string | number | boolean | null | undefined;
    });
    return valueMap;
  }, [customColumns, customFieldValues]);

  const customTableColumns: ColDef[] = customColumns
    .filter((col) => columnVisibility[col.name] !== false)
    .map((col) => ({
      field: col.name as string,
      headerName: col.label,
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      valueGetter: (params: ValueGetterParams<RowDataType>) => {
        return customFieldValueMap[params.data?.id || ""]?.[col.name] || "";
      },
      cellRenderer: (params: ICellRendererParams<RowDataType>) => {
        const value =
          customFieldValueMap[params.data?.id || ""]?.[col.name];
        if (value === null || value === undefined || value === "") return "";

        // Format based on type
        if (col.type === "date" && value) {
          return new Date(value as string).toLocaleDateString();
        }
        if (col.type === "boolean") {
          return value ? "Yes" : "No";
        }
        return String(value);
      },
    }));

  const columns: ColDef[] = baseColumns
    .filter((col) => {
      // Always show selection column
      if (col.field === "select") return true;
      // Filter based on visibility settings
      return columnVisibility[col.field as string] !== false;
    })
    .map((col) => {
      if (col.field === "status") {
        return {
          ...col,
          cellRenderer: (params: { value: string }) => (
            <Chip
              label={params.value}
              color={getStatusColor(params.value)}
              size="small"
              variant="outlined"
            />
          ),
        };
      }
      // Format date columns
      if (col.field === "due_date") {
        return {
          ...col,
          valueGetter: (params: ValueGetterParams<RowDataType>) => {
            return params.data?.due_date || "";
          },
          cellRenderer: (params: ICellRendererParams<RowDataType>) => {
            if (!params.value || typeof params.value !== "string") return "";
            return new Date(params.value).toLocaleDateString();
          },
        };
      }
      return col;
    });

  // Add selection column at the beginning
  const columnsWithSelection: ColDef[] = [
    {
      field: "select",
      headerName: "",
      width: 50,
      checkboxSelection: true,
      headerCheckboxSelection: true,
      pinned: "left" as const,
      sortable: false,
      filter: false,
      resizable: false,
      menuTabs: [],
    },
    ...columns,
    ...customTableColumns,
  ];

  const filteredOrders = useMemo(() => {
    let filtered = orders;

    // Apply status filter
    if (statusFilter) {
      filtered = filtered.filter((order) => order.status === statusFilter);
    }

    return filtered;
  }, [orders, statusFilter]);

  const ordersWithItemCount = useMemo(() => {
    return filteredOrders.map((order) => ({
      ...order,
      total_items: order.order_items?.length || 0,
      created_at: new Date(order.created_at)
        .toLocaleString("sv-SE", {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
        .replace(",", " -"),
    }));
  }, [filteredOrders]);

  // Handle row selection
  const onSelectionChanged = (event: SelectionChangedEvent) => {
    const selectedRows = event.api.getSelectedRows();
    const selectedIds = selectedRows.map((row) => (row as RowDataType).id);
    setSelectedOrders(selectedIds);
  };

  // Get selected order details
  const selectedOrderDetails = useMemo(() => {
    return orders.filter((order) => selectedOrders.includes(order.id));
  }, [orders, selectedOrders]);

  // Get valid status transitions for selected orders
  const getValidStatusTransitions = () => {
    if (selectedOrderDetails.length === 0) return [];

    const statuses = selectedOrderDetails.map((order) => order.status);
    const uniqueStatuses = [...new Set(statuses)];

    // If any order is completed or cancelled, no bulk updates allowed
    if (
      uniqueStatuses.some((status) =>
        ["completed", "cancelled"].includes(status)
      )
    ) {
      return [];
    }

    // More flexible transitions - allow direct completion from open status
    const availableTransitions = new Set<string>();

    if (uniqueStatuses.includes("open")) {
      availableTransitions.add("processing");
      availableTransitions.add("completed"); // Allow direct completion
      availableTransitions.add("cancelled");
    }

    if (uniqueStatuses.includes("processing")) {
      availableTransitions.add("completed");
      availableTransitions.add("cancelled");
    }

    return Array.from(availableTransitions);
  };

  // Clear backorder queue for an order
  const clearOrderBackorders = async (orderId: string) => {
    await supabase.from("backorder_queue").delete().eq("order_id", orderId);
  };

  // Release stock commitments for an order
  const releaseStockCommitments = async (orderId: string) => {
    // Get all stock commitments for this order
    const { data: commitments } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("order_id", orderId);

    if (commitments && commitments.length > 0) {
      // Group by inventory_id and calculate totals
      const inventoryUpdates = commitments.reduce((acc, commitment) => {
        const inventoryId = commitment.inventory_id;
        if (!acc[inventoryId]) {
          acc[inventoryId] = {
            quantityToAdd: 0,
            committedToZero: 0,
          };
        }
        acc[inventoryId].quantityToAdd += commitment.quantity_committed;
        acc[inventoryId].committedToZero += commitment.quantity_committed;
        return acc;
      }, {} as Record<string, InventoryUpdateData>);

      // Update inventory for each affected item
      for (const [inventoryId, updates] of Object.entries(inventoryUpdates)) {
        const { data: currentStock } = await supabase
          .from("inventory")
          .select("quantity, committed_quantity")
          .eq("id", inventoryId)
          .single();

        if (currentStock) {
          const updateData = updates as InventoryUpdateData;
          const newQuantity =
            currentStock.quantity + (updateData.quantityToAdd || 0);
          const newCommittedQuantity =
            currentStock.committed_quantity - updateData.committedToZero;

          await supabase
            .from("inventory")
            .update({
              quantity: newQuantity,
              committed_quantity: newCommittedQuantity,
            })
            .eq("id", inventoryId);
        }
      }

      // Mark commitments as released
      await supabase
        .from("stock_commitments")
        .update({
          status: "released",
          released_at: new Date().toISOString(),
        })
        .eq("order_id", orderId);
    }
  };

  // Update stock on order completion
  const updateStockOnOrderCompletion = async (orderId: string) => {
    // Get all stock commitments for this order
    const { data: commitments } = await supabase
      .from("stock_commitments")
      .select("*")
      .eq("order_id", orderId);

    if (commitments && commitments.length > 0) {
      // Group by inventory_id and calculate totals
      const inventoryUpdates = commitments.reduce((acc, commitment) => {
        const inventoryId = commitment.inventory_id;
        if (!acc[inventoryId]) {
          acc[inventoryId] = {
            quantityToSubtract: 0,
            committedToZero: 0,
          };
        }
        acc[inventoryId].quantityToSubtract += commitment.quantity_committed;
        acc[inventoryId].committedToZero += commitment.quantity_committed;
        return acc;
      }, {} as Record<string, InventoryUpdateData>);

      // Update inventory for each affected item
      for (const [inventoryId, updates] of Object.entries(inventoryUpdates)) {
        const { data: currentStock } = await supabase
          .from("inventory")
          .select("quantity, committed_quantity")
          .eq("id", inventoryId)
          .single();

        if (currentStock) {
          const updateData = updates as InventoryUpdateData;
          const newQuantity =
            currentStock.quantity - (updateData.quantityToSubtract || 0);
          const newCommittedQuantity =
            currentStock.committed_quantity - updateData.committedToZero;

          await supabase
            .from("inventory")
            .update({
              quantity: newQuantity,
              committed_quantity: newCommittedQuantity,
            })
            .eq("id", inventoryId);
        }
      }

      // Mark commitments as fulfilled
      for (const commitment of commitments) {
        await supabase
          .from("stock_commitments")
          .update({
            status: "fulfilled",
            quantity_fulfilled: commitment.quantity_committed,
            fulfilled_at: new Date().toISOString(),
          })
          .eq("id", commitment.id);
      }
    }
  };

  // CSV export handler
  const handleCSVExport = async () => {
    if (selectedOrders.length === 0) return;

    // Get selected order details
    const selectedOrdersData = selectedOrderDetails;

    // Prepare CSV data - one row per order item
    const csvHeaders = [
      "Order Number",
      "Customer Name",
      "Customer Email",
      "Status",
      "Created At",
      "Product ID",
      "Quantity Requested",
      "Quantity Fulfilled",
      "Item Status",
    ];

    const csvRows: string[][] = [];

    // Get all unique product IDs from selected orders
    const productIds = new Set<string>();
    selectedOrdersData.forEach((order) => {
      order.order_items?.forEach((item) => {
        if (item.product_id) {
          productIds.add(item.product_id);
        }
      });
    });

    // Fetch product details for all products
    const productDetails: Record<string, { name: string; sku: string }> = {};
    if (productIds.size > 0) {
      try {
        const { data: products } = await supabase
          .from("products")
          .select("id, name, sku")
          .in("id", Array.from(productIds));

        if (products) {
          products.forEach((product) => {
            productDetails[product.id] = {
              name: product.name,
              sku: product.sku,
            };
          });
        }
      } catch (error) {
        console.error("Error fetching product details:", error);
      }
    }

    selectedOrdersData.forEach((order) => {
      // If order has no items, create one row with order info only
      if (!order.order_items || order.order_items.length === 0) {
        csvRows.push([
          order.order_number || "",
          order.customer_name || "",
          order.customer_email || "",
          order.status || "",
          new Date(order.created_at).toLocaleString(),
          "No items",
          "0",
          "0",
          "",
        ]);
      } else {
        // Create one row per order item
        order.order_items.forEach((item) => {
          const product = productDetails[item.product_id];
          csvRows.push([
            order.order_number || "",
            order.customer_name || "",
            order.customer_email || "",
            order.status || "",
            new Date(order.created_at).toLocaleString(),
            product
              ? `${product.name} (${product.sku})`
              : `Product ID: ${item.product_id}`,
            item.quantity_requested?.toString() || "0",
            item.quantity_fulfilled?.toString() || "0",
            item.status || "pending",
          ]);
        });
      }
    });

    // Create CSV content
    const csvContent = [
      csvHeaders.join(","),
      ...csvRows.map((row) =>
        row
          .map((field) =>
            // Escape fields that contain commas or quotes
            typeof field === "string" &&
            (field.includes(",") || field.includes('"') || field.includes("\n"))
              ? `"${field.replace(/"/g, '""')}"`
              : field
          )
          .join(",")
      ),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `orders_export_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    const totalItems = csvRows.length;
    onInfoShow(
      `Exported ${selectedOrdersData.length} order(s) with ${totalItems} items to CSV`
    );
  };

  // Bulk status update handler
  const handleBulkStatusUpdate = async (newStatus: string) => {
    if (selectedOrders.length === 0) return;

    setIsBulkUpdating(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (const orderId of selectedOrders) {
        try {
          // Update order status
          const { updateOrderStatus } = useOrdersStore.getState();
          await updateOrderStatus(
            orderId,
            newStatus as "open" | "processing" | "completed" | "cancelled",
            baseUser!.id,
            baseUser!.organization_id!
          );

          if (newStatus === "cancelled") {
            // Clear backorders and release stock
            await clearOrderBackorders(orderId);
            await releaseStockCommitments(orderId);
          } else if (newStatus === "completed") {
            // Clear backorders, update stock, and mark items as delivered
            await clearOrderBackorders(orderId);
            await updateStockOnOrderCompletion(orderId);

            // Update order items to delivered
            const { data: orderItems } = await supabase
              .from("order_items")
              .select("id")
              .eq("order_id", orderId);

            if (orderItems) {
              for (const item of orderItems) {
                // Get the quantity_requested for this item
                const { data: itemData } = await supabase
                  .from("order_items")
                  .select("quantity_requested")
                  .eq("id", item.id)
                  .single();

                if (itemData) {
                  await supabase
                    .from("order_items")
                    .update({
                      quantity_fulfilled: itemData.quantity_requested,
                      status: "fulfilled",
                    })
                    .eq("id", item.id);
                }
              }
            }
          }

          successCount++;
        } catch (error) {
          console.error(`Error updating order ${orderId}:`, error);
          errorCount++;
        }
      }

      // Refresh orders
      await getOrdersByOrganizationId(baseUser!.organization_id!, false);

      if (successCount > 0) {
        onInfoShow(
          `Successfully updated ${successCount} order(s) to ${newStatus}`
        );
      }
      if (errorCount > 0) {
        onErrorShow(`Failed to update ${errorCount} order(s)`);
      }

      // Clear selection
      setSelectedOrders([]);
    } catch (error) {
      console.error("Bulk update error:", error);
      onErrorShow("Failed to update orders");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  return (
    <ViewAllContainer>
      <OrdersHeader>
        <Typography variant="h3">Orders</Typography>
        <IconButton
          onClick={() => setIsOpenCustomColumnsModal(true)}
          size="small"
          sx={{
            padding: 0,
            color: "text.secondary",
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
          title="Order Settings"
        >
          <Settings />
        </IconButton>
      </OrdersHeader>

      <ActionsContainer>
        <SearchActionsContainer>
          <InputField
            placeholder={"Search orders"}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{
              height: "26px",
            }}
          />
          {selectedOrders.length > 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "primary.main",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              {selectedOrders.length} selected
            </Typography>
          )}
        </SearchActionsContainer>
        <OtherActionsContainer>
          {selectedOrders.length > 0 ? (
            <>
              {getValidStatusTransitions().map((status) => (
                <Button
                  key={status}
                  variant="outlined"
                  size="small"
                  disabled={isBulkUpdating}
                  onClick={() => handleBulkStatusUpdate(status)}
                  color={status === "cancelled" ? "error" : "primary"}
                  sx={{
                    height: "26px",
                    mr: 1,
                  }}
                >
                  Mark as {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
              <Button
                variant="outlined"
                size="small"
                disabled={isBulkUpdating}
                onClick={handleCSVExport}
                sx={{
                  height: "26px",
                  mr: 1,
                }}
              >
                Export CSV
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setSelectedOrders([])}
                sx={{
                  height: "26px",
                  minWidth: "26px",
                  width: "26px",
                  padding: 0,
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "action.hover",
                  },
                }}
                title="Clear Selection"
              >
                <Close fontSize="small" />
              </Button>
            </>
          ) : (
            <>
              <Dropdown
                options={statusOptions}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as string)}
                placeholder="All Statuses"
                variant="outlined"
                size="small"
                sx={{
                  height: "26px",
                  minWidth: "140px",
                }}
              />
              <Button
                sx={{
                  height: "26px",
                }}
                variant="contained"
                onClick={() => {
                  open({ context: DRAWER_CONTEXT.CREATE_ORDER });
                }}
              >
                New Order
              </Button>
            </>
          )}
        </OtherActionsContainer>
      </ActionsContainer>

      <Divider />

      {loading && <TableLoadingIndicator />}
      {!loading && (
        <>
          {orders.length === 0 ? (
            <NoContentCard
              title="No orders"
              description="Create your first order to get started"
              buttonText="New Order"
              onClick={() => {
                open({ context: DRAWER_CONTEXT.CREATE_ORDER });
              }}
            />
          ) : (
            <AgGridTableContainer>
              <AgGridReact<RowDataType>
                rowData={ordersWithItemCount}
                columnDefs={columnsWithSelection}
                rowHeight={44}
                headerHeight={44}
                rowSelection="multiple"
                onSelectionChanged={onSelectionChanged}
                onRowClicked={(event) => {
                  // Don't navigate if clicking on checkbox
                  if (
                    event.event?.target instanceof HTMLElement &&
                    event.event.target.closest(".ag-checkbox")
                  ) {
                    return;
                  }
                  if (
                    event.data &&
                    typeof event.data === "object" &&
                    "id" in event.data
                  ) {
                    navigate(`/orders/order/${event.data.id}`);
                    return;
                  }
                  onErrorShow("Order not found, contact support");
                }}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  flex: 1,
                }}
                quickFilterText={debouncedSearchValue}
              />
            </AgGridTableContainer>
          )}
        </>
      )}

      <DeleteModal
        itemName={"order"}
        isOpen={isOpenConfirmationModal}
        handleClose={() => setIsOpenConfirmationModal(false)}
        onOk={async () => {
          if (selectedId) {
            await OrdersClient.deleteOrder(selectedId);
            setIsOpenConfirmationModal(false);
            getOrdersByOrganizationId(baseUser!.organization_id!, false);
            onInfoShow("Order deleted successfully");
          }
        }}
      />

      {/* Custom Columns Settings Modal */}
      <Dialog
        open={isOpenCustomColumnsModal}
        onClose={() => setIsOpenCustomColumnsModal(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "100%",
            margin: 0,
            height: "100%",
            maxHeight: "100%",
            borderRadius: 0,
            "@media (min-width: 600px)": {
              width: "auto",
              maxWidth: "900px",
              margin: "32px auto",
              height: "auto",
              maxHeight: "90vh",
              borderRadius: 1,
            },
          },
        }}
      >
        <CustomColumnsModalContainer>
          <CustomColumnsModalHeader>
            <Typography variant="h5">Order Settings</Typography>
            <IconButton
              onClick={() => setIsOpenCustomColumnsModal(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </CustomColumnsModalHeader>

          <Tabs
            value={settingsTab}
            onChange={(_, newValue) => setSettingsTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="Custom Columns" />
            <Tab label="Table Settings" />
          </Tabs>

          <CustomColumnsModalContent>
            {settingsTab === 0 && baseUser?.organization_id && (
              <CustomFieldsManager
                organizationId={baseUser.organization_id}
                entityType="order"
                entityLabel="Order"
                customFields={customColumns}
                isLoading={isLoadingColumns}
                onRefresh={fetchCustomColumns}
              />
            )}

            {settingsTab === 1 && (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Table Settings
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  Choose which columns to display in the orders table.
                </Typography>

                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 2,
                    mb: 3,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 2 }}>
                    Standard Columns
                  </Typography>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                  >
                    {Object.entries(ordersFieldAndHeaderNames).map(
                      ([field, label]) => (
                        <FormControlLabel
                          key={field}
                          control={
                            <Checkbox
                              checked={columnVisibility[field] !== false}
                              onChange={(e) =>
                                handleColumnVisibilityChange(
                                  field,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={label}
                        />
                      )
                    )}
                  </Box>
                </Box>

                {/* Custom Metadata Columns */}
                {customColumns.length > 0 && (
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: "background.paper",
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      Custom Columns
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      {customColumns.map((column) => (
                        <FormControlLabel
                          key={column.id}
                          control={
                            <Checkbox
                              checked={columnVisibility[column.name] !== false}
                              onChange={(e) =>
                                handleColumnVisibilityChange(
                                  column.name,
                                  e.target.checked
                                )
                              }
                            />
                          }
                          label={`${column.label} (${column.type})`}
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CustomColumnsModalContent>
        </CustomColumnsModalContainer>
      </Dialog>
    </ViewAllContainer>
  );
}

const ViewAllContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  width: "100%",
  overflow: "hidden",
  minHeight: 0,
  height: "calc(100vh - 48px)",
});

const OrdersHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: " 16px 24px",
  background: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.divider}`,
  height: "auto",
  flexWrap: "wrap",
}));

const ActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: "auto",
  flexWrap: "wrap",
  width: "100%",
  padding: "12px 24px",
  justifyContent: "space-between",
  flexDirection: "column",
  gap: ".5rem",
  [theme.breakpoints.up("md")]: {
    flexDirection: "row",
  },
}));

const SearchActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: ".5rem",
  justifyContent: "flex-start",
  width: "100%",
  [theme.breakpoints.up("md")]: {
    width: "auto",
  },
}));

const OtherActionsContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  gap: ".5rem",
  flexWrap: "wrap",

  [theme.breakpoints.up("md")]: {
    justifyContent: "flex-end",
    width: "auto",
  },
}));

const CustomColumnsModalContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  "@media (min-width: 600px)": {
    padding: theme.spacing(3),
    minWidth: "500px",
    height: "auto",
  },
}));

const CustomColumnsModalHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(2),
  flexShrink: 0,
}));

const CustomColumnsModalContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  flex: 1,
  overflow: "auto",
}));
